<template>
  <div style="width: 100%">
    <v-card
      v-if="relatorio"
      flat
      class="pa-2"
    >
      <!-- Atuação Profissional -->
      <v-row>
        <v-col cols="12">
          <app-title texto="ATUAÇÃO PROFISSIONAL" />
        </v-col>
      </v-row>

      <!-- Cargos e Funções -->
      <v-row>
        <v-col cols="12">
          <app-title texto="CARGO / FUNÇÃO" cor="secondary" />
        </v-col>
      </v-row>
      <v-row>
        <v-col lg="6" md="12" sm="12">
          <pie-chart
            titulo="Comparativo de Cargos"
            :fonte="`Fonte: Sistema Desenvolva, ${ano}`"
            :dados="dadosPieChartCargo.series"
            :dados-complementares="dadosPieChartCargo.subSeries"
            class="chart"
            :is-get-svg="isGetSvg.cargo"
            @svg="(value) => svg.cargo = value"
          />
        </v-col>
        <v-col lg="6" md="12" sm="12">
          <pie-chart
            titulo="Comparativo de Funções"
            :fonte="`Fonte: Sistema Desenvolva, ${ano}`"
            :dados="dadosPieChartFuncao.series"
            :dados-complementares="dadosPieChartFuncao.subSeries"
            class="chart"
            :is-get-svg="isGetSvg.funcao"
            @svg="(value) => svg.funcao = value"
          />
        </v-col>
      </v-row>

      <!-- Resultados -->
      <v-row>
        <v-col cols="12">
          <app-title texto="RESULTADOS" cor="secondary" />
        </v-col>
        <v-col cols="12">
          <tabela-resultado
            :relatorio="relatorio"
          />
        </v-col>
      </v-row>

      <!-- Relatório PDF -->
      <v-row
        class="justify-end mb-1"
      >
        <v-tooltip
          max-width="400"
          top
        >
          <template v-slot:activator="{ on }">
            <v-btn
              color="success"
              @click="getRelatorio()"
              v-on="on"
            >
              <v-icon>get_app</v-icon>
              BAIXAR PDF
            </v-btn>
          </template>
          <span>Clique para baixar o relatório dos dados em formato PDF.</span>
        </v-tooltip>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import AppTitle from '@/components/core/AppTitle';
import PieChart from '@/components/chart/PieChart';
import { generatePDF, preparePdf, convertSvg } from '@/modules/relatorioPDF';
import TabelaResultado from './TabelaResultadoGeralProfissional';

export default {
  name: 'RelatorioPerfilGeralProfissional',

  components: {
    AppTitle,
    PieChart,
    TabelaResultado,
  },

  props: {
    relatorio: {
      type: Object,
      required: true,
    },
    ano: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isGetSvg: {
        cargo: false,
        funcao: false,
      },
      svg: {
        cargo: null,
        funcao: null,
      },
    };
  },

  computed: {
    dadosPieChartCargo() {
      return {
        series: [
          {
            name: 'Cargos',
            data: this.relatorio.cargos.map((c) => ({
              name: c.nome,
              y: c.quantidade,
              drilldown: c.id,
            })).sort((a, b) => {
              if (a.y < b.y) { return 1; }
              if (a.y > b.y) { return -1; }
              return 0;
            }),
          },
        ],
        subSeries: this.relatorio.cargos.map((c) => ({
          name: c.nome,
          id: c.id,
          data: this.relatorio.unidades.map((u) => {
            const cargo = u.cargos.find((c2) => c2.id === c.id);
            return {
              name: u.sigla,
              y: cargo ? cargo.quantidade : 0,
            };
          }).filter((u) => u.y > 0)
            .sort((a, b) => {
              if (a.y < b.y) { return 1; }
              if (a.y > b.y) { return -1; }
              return 0;
            }),
        })),
      };
    },
    dadosPieChartFuncao() {
      return {
        series: [
          {
            name: 'Funções',
            data: this.relatorio.funcoes.map((f) => ({
              name: f.nome,
              y: f.quantidade,
              drilldown: f.id,
            })).sort((a, b) => {
              if (a.y < b.y) { return 1; }
              if (a.y > b.y) { return -1; }
              return 0;
            }),
          },
        ],
        subSeries: this.relatorio.funcoes.map((f) => ({
          name: f.nome,
          id: f.id,
          data: this.relatorio.unidades.map((u) => {
            const funcao = u.funcoes.find((f2) => f2.id === f.id);
            return {
              name: u.sigla,
              y: funcao ? funcao.quantidade : 0,
            };
          }).filter((u) => u.y > 0)
            .sort((a, b) => {
              if (a.y < b.y) { return 1; }
              if (a.y > b.y) { return -1; }
              return 0;
            }),
        })),
      };
    },
  },

  methods: {
    async getRelatorio() {
      this.isGetSvg.cargo = true;
      this.isGetSvg.funcao = true;
      await preparePdf(async () => {
        const documento = {
          nome: 'relatorio-geral-atuacao-profissional-perfil',
          cabecalho: {
            titulo: 'Relatório Geral da Atuação Profissional',
            itens: [
              `Unidade(s): ${this.relatorio.unidades.map((u) => u.sigla).join(', ')}`,
            ],
          },
          blocos: [
            {
              titulo: 'GRÁFICOS',
              subtitulo: 'CARGO / FUNÇÃO',
              itens: [
                { tipo: 'imagem', valor: await convertSvg(this.svg.cargo) },
                { tipo: 'imagem', valor: await convertSvg(this.svg.funcao) },
              ],
            },
            {
              titulo: 'RESULTADOS',
              subtitulo: 'CARGO / FUNÇÃO',
              itens: [{
                tipo: 'tabela',
                valor: {
                  colunas: [
                    'UNIDADE',
                    'CARGOS',
                    'FUNÇÕES',
                    'TOTAL DE SERVIDORES',
                  ],
                  linhas: this.relatorio.unidades.map((u) => ([
                    `${u.nome} (${u.sigla})`,
                    u.cargos.length,
                    u.funcoes.length,
                    u.quantidadePerfis,
                  ])),
                  rodape: [
                    'TOTAL',
                    this.relatorio.quantidadeCargos,
                    this.relatorio.quantidadeFuncoes,
                    this.relatorio.quantidadePerfis,
                  ],
                },
              }],
            },
          ],
        };
        generatePDF(documento);
      });
      this.isGetSvg.cargo = false;
      this.isGetSvg.funcao = false;
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  max-height: 400px;
  font-family: Roboto, sans-serif;
}
</style>
